import clsx from 'clsx';
import { uniqBy } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next/pages';

import { CityWithJobCount } from '@pflegia/data-access';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { JOBS } from '@pflegia/ui/pflegia/constants/routes';
import { ContainerSection } from '@pflegia/ui/shared/container-section/ContainerSection';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';

import ClusterButton from '@shared/components/cluster-button/ClusterButton';
import CardsVariant from '@shared/components/position-cluster/CardsVariant';
import { CITY_CLUSTER, POSITION_CLUSTER_CARDS } from '@shared/constants/homepage-clusters';

interface JobTitleCityLinks {
  cities: CityWithJobCount[];
  jobTitle: string;
  jobTitleSlug: string;
}

interface NavigationClusterProps {
  location?: string;
  nearbyCities?: CityWithJobCount[];
  hasPosition?: boolean;
  jobTitleCityLinks?: JobTitleCityLinks;
}

interface CityLink {
  label: string;
  href: string;
}

const getCityLabels = (nearbyCities?: CityWithJobCount[], location?: string) => {
  if (nearbyCities?.length) return nearbyCities.map(({ city }) => city);
  return location ? [] : CITY_CLUSTER;
};

const getCityLinks = ({ location, nearbyCities, jobTitleCityLinks }: NavigationClusterProps): CityLink[] => {
  if (jobTitleCityLinks) {
    return uniqBy(
      jobTitleCityLinks.cities.map(({ city }) => ({
        label: city,
        href: `${JOBS}/${jobTitleCityLinks.jobTitleSlug}/${normalizeJobsParams(city)}`,
      })),
      'href',
    );
  }

  return getCityLabels(nearbyCities, location).map(city => ({ label: city, href: `${JOBS}/${normalizeJobsParams(city)}` }));
};

const getCitiesTitleKey = (location?: string, jobTitleCityLinks?: JobTitleCityLinks) => {
  if (jobTitleCityLinks) return 'jobTitleCitiesTitle';
  return location ? 'citiesTitle' : 'citiesTitleWithoutLocation';
};

const NavigationCluster = ({ location, nearbyCities, hasPosition, jobTitleCityLinks }: NavigationClusterProps) => {
  const { t } = useTranslation('jobBoard', { keyPrefix: 'internalLinkingClusters' });
  const { sourceDomainName } = useFlavorFromRoute();
  const cityLinks = getCityLinks({ location, nearbyCities, jobTitleCityLinks });
  const shouldShowCityLinks = cityLinks.length > 0 && (hasPosition || !location);
  const shouldShowPositionCards = Boolean(location && hasPosition);

  if (!shouldShowCityLinks && !shouldShowPositionCards) return null;

  return (
    <ContainerSection className={clsx('mt-12 flex flex-col md:mt-32', { 'mb-12': location })}>
      {shouldShowCityLinks && (
        <>
          <h2 className='mb-5 text-center text-2xl md:text-4xl'>
            <Trans
              components={{
                span: <span className='font-handwriting text-main-blue align-middle md:text-5xl' />,
              }}
              i18nKey={getCitiesTitleKey(location, jobTitleCityLinks)}
              values={{ domain: sourceDomainName, jobTitle: jobTitleCityLinks?.jobTitle }}
              t={t}
            />
          </h2>
          <div className='mb-16 flex flex-wrap justify-center md:mb-32'>
            {cityLinks.map(({ label, href }) => (
              <ClusterButton key={href} label={label} href={href} testId={`city-${label}`} />
            ))}
          </div>
        </>
      )}

      {shouldShowPositionCards && (
        <>
          <h2 className='mb-5 text-center text-2xl md:text-4xl'>
            <Trans
              components={{
                span: <span className='font-handwriting text-main-blue align-middle md:text-5xl' />,
              }}
              i18nKey='moreJobsTitle'
              values={{ domain: sourceDomainName }}
              t={t}
            />
          </h2>
          <CardsVariant cards={POSITION_CLUSTER_CARDS} location={location} />
        </>
      )}
    </ContainerSection>
  );
};

export default NavigationCluster;
